import React, { useState, useEffect, useCallback } from 'react'
import { dealerDocSettingsApi } from '../../services/dealerDocSettings'
import type { DealerDocSetting } from '../../services/dealerDocSettings'
import { Toast } from '../shared/Toast'

function ConnectionStatus({ syncStatus, lastSyncAt }: { syncStatus: string; lastSyncAt: string | null }) {
  const statusConfig: Record<string, { label: string; color: string; bgColor: string; dotColor: string }> = {
    idle: { label: 'Connected', color: 'text-green-700', bgColor: 'bg-green-50 border-green-200', dotColor: 'bg-green-500' },
    syncing: { label: 'Syncing', color: 'text-yellow-700', bgColor: 'bg-yellow-50 border-yellow-200', dotColor: 'bg-yellow-500' },
    error: { label: 'Error', color: 'text-red-700', bgColor: 'bg-red-50 border-red-200', dotColor: 'bg-red-500' },
  }

  const config = statusConfig[syncStatus] || statusConfig.idle

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr)
    return date.toLocaleString()
  }

  return (
    <div className={`rounded-lg border px-4 py-3 ${config.bgColor}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${config.dotColor} ${syncStatus === 'syncing' ? 'animate-pulse' : ''}`} />
          <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
        </div>
        <span className="text-xs text-grey">
          Last sync: {formatDate(lastSyncAt)}
        </span>
      </div>
    </div>
  )
}

export function DealerDocSettings(): React.ReactElement | null {
  const [setting, setSetting] = useState<DealerDocSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  // Local form state
  const [formEnabled, setFormEnabled] = useState(false)
  const [formDealerCode, setFormDealerCode] = useState('')
  const [formApiKey, setFormApiKey] = useState('')
  const [formAutoSync, setFormAutoSync] = useState(false)

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type })
  }, [])

  const syncFormState = useCallback((s: DealerDocSetting) => {
    setFormEnabled(s.enabled)
    setFormDealerCode(s.dealer_code || '')
    setFormApiKey('')
    setFormAutoSync(s.auto_sync)
    setHasChanges(false)
  }, [])

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await dealerDocSettingsApi.show()
      setSetting(data)
      syncFormState(data)
    } catch {
      setError('Failed to load DealerDoc settings. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [syncFormState])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const markChanged = () => setHasChanges(true)

  const handleSave = async () => {
    try {
      setSaving(true)
      const params: { enabled?: boolean; api_key?: string; dealer_code?: string; auto_sync?: boolean } = {
        enabled: formEnabled,
        dealer_code: formDealerCode || undefined,
        auto_sync: formAutoSync,
      }
      if (formApiKey) {
        params.api_key = formApiKey
      }
      const updated = await dealerDocSettingsApi.update(params)
      setSetting(updated)
      syncFormState(updated)
      showToast('DealerDoc settings saved', 'success')
    } catch {
      showToast('Failed to save DealerDoc settings', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12">
        <div className="flex items-center justify-center gap-3 text-grey">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          <span className="text-sm">Loading DealerDoc settings...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm text-grey mb-4">{error}</p>
        <button onClick={loadSettings} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90">
          Try Again
        </button>
      </div>
    )
  }

  if (!setting) return null

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-secondary">DealerDoc Integration</h1>
          <p className="text-sm text-grey mt-1">Configure DealerDoc USA integration for document management.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Connection Status */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">Connection Status</h2>
        </div>
        <div className="p-6">
          <ConnectionStatus syncStatus={setting.sync_status} lastSyncAt={setting.last_sync_at} />
        </div>
      </div>

      {/* Configuration */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">Configuration</h2>
        </div>
        <div className="p-6 space-y-5">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-secondary">Enable Integration</label>
              <p className="text-xs text-grey mt-0.5">Enable or disable DealerDoc integration.</p>
            </div>
            <button
              onClick={() => { setFormEnabled(!formEnabled); markChanged() }}
              role="switch"
              aria-checked={formEnabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 cursor-pointer ${
                formEnabled ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                formEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Dealer Code */}
          <div>
            <label htmlFor="dealer-code" className="block text-sm font-medium text-secondary mb-1.5">Dealer Code</label>
            <input
              id="dealer-code"
              type="text"
              value={formDealerCode}
              onChange={(e) => { setFormDealerCode(e.target.value); markChanged() }}
              placeholder="Enter your DealerDoc dealer code"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
            />
          </div>

          {/* API Key */}
          <div>
            <label htmlFor="api-key" className="block text-sm font-medium text-secondary mb-1.5">API Key</label>
            <input
              id="api-key"
              type="password"
              value={formApiKey}
              onChange={(e) => { setFormApiKey(e.target.value); markChanged() }}
              placeholder={setting.api_key_set ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022' : 'Enter your DealerDoc API key'}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
            />
            {setting.api_key_set && !formApiKey && (
              <p className="text-xs text-grey mt-1">API key is set. Enter a new value to update it.</p>
            )}
          </div>

          {/* Auto Sync Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-secondary">Auto Sync</label>
              <p className="text-xs text-grey mt-0.5">Automatically sync documents with DealerDoc.</p>
            </div>
            <button
              onClick={() => { setFormAutoSync(!formAutoSync); markChanged() }}
              role="switch"
              aria-checked={formAutoSync}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 cursor-pointer ${
                formAutoSync ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                formAutoSync ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
